import axios from "axios";
import { useEffect, useState } from "react";
import { FaEye, FaTrash, FaUserEdit } from "react-icons/fa";
import { IoPeopleSharp } from "react-icons/io5";
import { MdGroupAdd } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 7;

  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const rc_id = sessionStorage.getItem('rc_id'); // set in Login
      const response = await axios.get("/auth/users/customers", {
        params: { rc_id }
      });
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };


  const handleDelete = async () => {
    if (!selectedCustomer) return;

    try {
      await axios.delete(`/auth/users/${selectedCustomer.id}`);
      const updatedCustomers = customers.filter(c => c.id !== selectedCustomer.id);
      setCustomers(updatedCustomers);
      setShowDeleteModal(false);
      toast.success(`Customer "${selectedCustomer.name}" deleted successfully.`);

      if ((currentPage - 1) * recordsPerPage >= updatedCustomers.length) {
        setCurrentPage(prev => Math.max(prev - 1, 1));
      }
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error("Failed to delete customer. Please try again.");
    }
  };

  // Filter logic
  const filteredCustomers = customers.filter(customer => {
    const name = customer.name ? customer.name.toLowerCase() : "";
    const email = customer.email ? customer.email.toLowerCase() : "";
    const accountNumber = customer.accountNumber ? customer.accountNumber.toString() : "";

    return (
      name.includes(searchTerm.toLowerCase()) ||
      email.includes(searchTerm.toLowerCase()) ||
      accountNumber.includes(searchTerm)
    );
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredCustomers.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredCustomers.slice(indexOfFirstRecord, indexOfLastRecord);

  // Pagination page numbers like DriverPayments.jsx
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 6) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
      }
    }
    return pages;
  };

  return (
    <div className="p-4">
      <div className="bg-white shadow-md rounded-2xl p-5 w-full max-w-full">
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between sm:gap-0 mb-6 px-4 sm:px-0">
          <h2 className="flex items-center gap-2 text-xl sm:text-2xl font-bold text-green-800">
            <IoPeopleSharp className="text-2xl sm:text-3xl" />
            Customer List
          </h2>
          <button
            onClick={() => navigate('/redem-addcustomer')}
            className="flex items-center justify-center gap-1 sm:gap-2 bg-green-600 text-white px-3 py-1 rounded-md text-sm sm:text-base hover:bg-green-700"
          >
            <MdGroupAdd className="text-lg sm:text-xl" />
            <span>Add Customer</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="flex justify-end mb-4 px-4 sm:px-0">
          <input
            type="text"
            placeholder="Search by name, email or account no..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page when searching
            }}
            className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>

        <div className="w-full overflow-x-auto">
          <table className="min-w-max w-full text-sm border border-gray-200">
            <thead className="bg-lime-100 text-green-900 uppercase font-semibold">
              <tr>
                <th className="px-4 py-3">S.no</th>
                <th className="px-4 py-3">Acc. No</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Address</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone Number</th>
                <th className="px-2 py-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRecords.length > 0 ? currentRecords.map((customer, index) => (
                <tr key={customer.id} className="bg-white border-b hover:bg-gray-50">
                  <td className="px-4 py-4 font-medium text-gray-900">{indexOfFirstRecord + index + 1}</td>
                  <td className="px-4 py-4 font-medium text-red-500"># {customer.accountNumber}</td>
                  <td className="px-4 py-4">{customer.name}</td>
                  <td className="px-4 py-4">{customer.address}</td>
                  <td className="px-4 py-4">{customer.email}</td>
                  <td className="px-4 py-4">{customer.phone}</td>
                  <td className="px-4 py-4 flex justify-center gap-2">
                    <button
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => navigate(`/redem-viewcustomer/${customer.id}`)}
                    >
                      <FaEye />
                    </button>
                    <button
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => navigate(`/redem-editcustomer/${customer.id}`)}
                    >
                      <FaUserEdit />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCustomer(customer);
                        setShowDeleteModal(true);
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" className="text-center py-4 text-gray-500">
                    No customers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4 gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              &lt;
            </button>

            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={index} className="px-3 py-1">...</span>
              ) : (
                <button
                  key={index}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 border rounded ${currentPage === page ? "bg-[#a3e635] text-white" : ""}`}
                >
                  {page}
                </button>
              )
            )}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              &gt;
            </button>
          </div>
        )}
      </div>

      <ToastContainer position="top-right" autoClose={3000} />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-xl w-full max-w-sm">
            <h3 className="text-lg font-semibold text-red-600 mb-4">Delete Confirmation</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete <strong>{selectedCustomer.name}</strong>?
            </p>
            <div className="flex justify-end gap-4">
              <button
                className="px-4 py-2 rounded-md bg-gray-300 hover:bg-gray-400"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
                onClick={handleDelete}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerList;
